import { sleep } from "../../../utilities/sleep.ts";
import {
  FilterName,
  FilterState,
  Message,
  Reference,
  Research,
  ResearchId,
} from "../state/AppState.ts";
import { Result } from "../../http/HttpClient.ts";
import { RESEARCH_TYPES } from "../../../config/constants.ts";
import { ApiService } from "./ApiService.interface.ts";
import { makeAutoSummary } from "./makeAutoSummary.ts";

type InMemResearch = Research & {
  messages: Message[];
  references?: Reference[];
};

/**
 * A simple and FAKE in-memory implementation of the ApiService interface.
 * This implementation is used to simulate the behavior of the real API.
 */
export class InMemoryApiService implements ApiService {
  private researches: InMemResearch[] = [];

  constructor() {
    console.log("Using InMemoryApiService");
    const researchesString = localStorage.getItem("researches");
    if (researchesString) {
      this.researches = JSON.parse(researchesString);
    }
  }

  async getResearchTypes(): Result<
    { name: string; display_name: string; description: string }[]
  > {
    return {
      success: true,
      data: [
        {
          name: RESEARCH_TYPES.GENERATIVE_SUMMARIZATION,
          display_name: "Pro - Enriched",
          description:
            "Each document is scanned individually against your specific brief and aggregated into a universal output. This process can take time, especially with larger datasets, but ensures that each document is passed through the AI for analysis at least once",
        },
        {
          name: RESEARCH_TYPES.DUPLICATE_DOCUMENTS,
          display_name: "Duplicate Documents Analysis",
          description:
            "Meta Analysis summarization uses generative LLM, summarizing across relevant document chunks found during document Meta Analysis",
        },
        {
          name: RESEARCH_TYPES.SEMANTIC_ONLY,
          display_name: "Zip - Semantic",
          description:
            "Brief responses are generated using only documents which share semantic similarity to the input brief. Use this option when speed is important or you are performing general Q&A against a dataset",
        },
      ],
      httpStatus: 200,
    };
  }

  async deleteResearch(payload: { researchId: ResearchId }): Result {
    const researchIndex = this.researches.findIndex(
      (r) => r.id === payload.researchId,
    );
    if (researchIndex === -1) {
      return { success: false, httpStatus: 404 };
    }
    this.researches.splice(researchIndex, 1);
    this._saveLocal();
    return { success: true, httpStatus: 200 };
  }

  async renameResearch(payload: {
    researchId: ResearchId;
    name: string;
  }): Result<{ research: Research }> {
    const research = this.researches.find((r) => r.id === payload.researchId);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    research.display_name = payload.name;
    this._saveLocal();
    return {
      success: true,
      data: { research: structuredClone(research) },
      httpStatus: 200,
    };
  }

  regenerateSummary(param: { researchId: ResearchId }): Result {
    console.log(param);
    throw new Error("Method not implemented.");
  }

  setFilters(): Result {
    throw new Error("Method setFilters not implemented.");
  }

  getFilterOptions(payload: { researchId: string }): Result<{
    filterOptions: Record<FilterName, { enabled: boolean; value: string[] }>;
  }> {
    console.log(payload);
    throw new Error("Method getFilterOptions not implemented.");
  }

  getFilters(payload: { researchId: string }): Result<FilterState> {
    console.log(payload);
    throw new Error("Method getFilters not implemented.");
  }

  async getKnowledgeSources(): Result<{
    knowledgeSources: {
      name: string;
      repoUrl?: string;
    }[];
  }> {
    return {
      httpStatus: 200,
      success: true,
      data: {
        knowledgeSources: [
          {
            name: "Sample Knowledge Source 1",
            repoUrl: "https://example.com/knowledge-source-1",
          },
        ],
      },
    };
  }

  async getFiltersByKnowledgeSource(payload: {
    knowledgeSourceName: string;
  }): Result<{
    filters: {
      label: string;
      key: string;
      type: "selection_multiple" | "month_range";
      custom: boolean;
      options?: { label: string; value: string }[];
    }[];
  }> {
    console.log(payload);
    return {
      httpStatus: 200,
      success: true,
      data: {
        filters: [
          {
            label: "Sample Filter",
            key: "sample_filter",
            type: "selection_multiple",
            custom: false,
            options: [
              { label: "Option 1", value: "option_1" },
              { label: "Option 2", value: "option_2" },
            ],
          },
        ],
      },
    };
  }

  async createResearch(payload: {
    knowledgeSourceId: string;
    researchGoal: string;
    additionalQuestions: string;
    researchType?: string;
  }): Result<{ id: ResearchId }> {
    const id = this.researches.length.toString() as ResearchId;
    const research: InMemResearch = {
      id,
      display_name: "",
      knowledge_source: payload.knowledgeSourceId,
      brief: payload.researchGoal,
      questions: payload.additionalQuestions,
      research_type: payload.researchType,
      create_time: new Date().toISOString(),
      update_time: new Date().toISOString(),
      status: "processing_meta_analysis",
      messages: [],
    };
    this.researches.push(research);

    this._saveLocal();
    await sleep(1000);
    // Simulate the completion of the research (async)
    this._finishSummary({
      researchId: id,
      summary: makeAutoSummary({
        researchGoal: payload.researchGoal,
        additionalQuestions: payload.additionalQuestions,
      }),
    }).then();
    return { success: true, data: { id }, httpStatus: 202 };
  }

  async createChatFirstResearch(payload: {
    initialMessage: string;
    knowledgeSourceId?: string;
  }): Result<{ id: ResearchId }> {
    const id = this.researches.length.toString() as ResearchId;
    const research: InMemResearch = {
      id,
      display_name: payload.initialMessage.substring(0, 50),
      knowledge_source: payload.knowledgeSourceId || "default",
      brief: payload.initialMessage,
      questions: "",
      create_time: new Date().toISOString(),
      update_time: new Date().toISOString(),
      status: "completed",
      messages: [],
    };
    this.researches.push(research);

    this._saveLocal();
    return { success: true, data: { id }, httpStatus: 202 };
  }

  async getResearch(payload: { id: string }): Result<Research> {
    const research = this.researches.find((r) => r.id === payload.id);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    return { success: true, data: structuredClone(research), httpStatus: 200 };
  }

  async getResearchList(): Result<{ researches: Research[] }> {
    return {
      success: true,
      data: { researches: structuredClone(this.researches) },
      httpStatus: 200,
    };
  }

  async submitMessage(payload: {
    researchId: string;
    message: string;
  }): Result<{
    messageId: string;
    responseId: string;
  }> {
    const research = this.researches.find((r) => r.id === payload.researchId);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    const messageId: string = research.messages.length.toString();
    research.messages.push({
      id: messageId,
      value: payload.message,
      status: "completed",
      sender: "user",
      create_time: new Date().toISOString(),
    });
    await sleep(1000);
    const messageValue: string = `Response to message ${messageId}, content was: "${payload.message}"`;
    research.messages.push({
      id: messageId + "-response",
      status: "completed",
      sender: "agent",
      create_time: new Date().toISOString(),
      value: messageValue,
      citations: [
        {
          pointer: messageValue.indexOf(" to message"),
          referenceIds: [messageId + "-citation_1"],
        },
        {
          pointer: messageValue.indexOf(" was:"),
          referenceIds: [messageId + "-citation_1"],
        },
      ],
      references: [
        {
          id: messageId + "-citation_1",
          title: "2022 Pet Food Consumer Sentiment Report.pdf",
          page: "Page 3",
          excerpt: "The average pet owner spends $100 per month on pet food.",
        },
      ],
    });

    this._saveLocal();
    return {
      success: true,
      data: {
        messageId,
        responseId: messageId + "-response",
      },
      httpStatus: 200,
    };
  }

  async getResearchMessages(payload: { researchId: string }) {
    const research = this.researches.find((r) => r.id === payload.researchId);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    const promise: Result<{ messages: Message[] }> = new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: { messages: structuredClone(research.messages) },
          httpStatus: 200,
        });
      }, 1000);
    });
    return promise;
  }

  async getMessage(payload: {
    researchId: string;
    messageId: string;
  }): Result<{ message: Message }> {
    const research = this.researches.find((r) => r.id === payload.researchId);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    const message = research.messages.find((m) => m.id === payload.messageId);
    if (!message) {
      return { success: false, httpStatus: 404 };
    }
    await sleep(100);
    return {
      success: true,
      data: { message: structuredClone(message) },
      httpStatus: 200,
    };
  }

  async getReferences(payload: {
    researchId: string;
  }): Result<{ references: Reference[] }> {
    const research = await this._getResearch(payload);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    const existingReferences = research?.references;
    await sleep(200);
    if (!existingReferences) {
      research.references = this._makeReferences(research.id);
    }
    return {
      success: true,
      data: structuredClone({ references: research.references! }),
      httpStatus: 200,
    };
  }

  async removeReferences(payload: {
    researchId: string;
    referenceIds: string[];
  }): Result {
    const research = await this._getResearch({
      researchId: payload.researchId,
    });
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    research.references = research.references?.filter(
      (ref) => !payload.referenceIds.includes(ref.id),
    );
    this._saveLocal();
    await sleep(500);
    return {
      success: true,
      httpStatus: 200,
    };
  }

  private async _finishSummary(payload: {
    researchId: string;
    summary: string;
  }) {
    await sleep(1000);
    const research = this.researches.find((r) => r.id === payload.researchId);
    if (!research) {
      return { success: false, httpStatus: 404 };
    }
    research.messages.push({
      id: "summary",
      status: "completed",
      value: payload.summary,
      sender: "agent",
      create_time: new Date().toISOString(),
    });
    research.status = "completed";
    research.update_time = new Date().toISOString();
    this._saveLocal();
  }

  private async _getResearch(payload: {
    researchId: string;
  }): Promise<InMemResearch | undefined> {
    await sleep(300);
    return this.researches.find(
      (research) => research.id === payload.researchId,
    );
  }

  private _saveLocal() {
    localStorage.setItem("researches", JSON.stringify(this.researches));
  }

  private _makeReferences(researchId: string): {
    id: string;
    title: string;
    page: string;
    excerpt: string;
  }[] {
    return Array.from({ length: 10 }, (_, i) => ({
      id: researchId + "_" + i.toString(),
      title: "2022 Pet Food Consumer Sentiment Report.pdf",
      page: i % 2 === 0 ? "Page 3" : "Slide 5",
      excerpt: "The average pet owner spends $100 per month on pet food.",
    }));
  }

  async getTermsAndConditionsStatus(): Result<{
    status: "pending_acceptance" | "accepted" | "acceptance_not_required";
  }> {
    await sleep(200);
    const status =
      localStorage.getItem("termsAndConditionsStatus") || "pending_acceptance";
    return {
      success: true,
      data: {
        status: status as
          | "pending_acceptance"
          | "accepted"
          | "acceptance_not_required",
      },
      httpStatus: 200,
    };
  }

  async acceptTermsAndConditions(): Result<{ status: "accepted" }> {
    await sleep(500);
    localStorage.setItem("termsAndConditionsStatus", "accepted");
    return {
      success: true,
      data: { status: "accepted" },
      httpStatus: 200,
    };
  }

  async getLatestTermsAndConditions(): Result<{ content: string }> {
    await sleep(300);
    const mockTermsContent = `# Terms and Conditions

## 1. Acceptance of Terms

By using this application, you agree to comply with and be bound by the following terms and conditions.

## 2. Use of Application

- You will use this application in accordance with all applicable laws and regulations.
- You agree to maintain the confidentiality of any sensitive information accessed through this application.

## 3. Data Privacy

We are committed to protecting your privacy and handling your data responsibly.

**Last updated:** ${new Date().toLocaleDateString()}`;

    return {
      success: true,
      data: { content: mockTermsContent },
      httpStatus: 200,
    };
  }
}
